import { concat, isNumber, map, range, reduce, uniqBy } from 'lodash'
import { forwardRef, useMemo } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type HeightInput_query$key } from '@/__generated__/HeightInput_query.graphql'
import {
  SingleSelectInput,
  type SelectOption,
  type SingleSelectInputProps,
  type SingleSelectInputRef
} from '@/components'
import { useHasMetricLocale } from '@/hooks'
import { HEIGHT_MAX, HEIGHT_MID, HEIGHT_MIN } from '@/lib'

export interface HeightInputProps
  extends Omit<SingleSelectInputProps, 'displayValue' | 'flatListProps' | 'options' | 'value' | 'ref'> {
  queryRef: HeightInput_query$key | null | undefined
  value?: number | null | undefined
}

export const HeightInput = forwardRef<SingleSelectInputRef, HeightInputProps>(
  ({ placeholder, queryRef, value, ...props }, ref) => {
    const hasMetricLocale = useHasMetricLocale()
    const queryData = useFragment(
      graphql`
        fragment HeightInput_query on Query {
          currentUser {
            isMetricSystem
          }
        }
      `,
      queryRef
    )
    const isMetricSystem = useMemo(
      () => queryData?.currentUser?.isMetricSystem ?? hasMetricLocale,
      [hasMetricLocale, queryData?.currentUser?.isMetricSystem]
    )
    const options = useMemo(
      () =>
        uniqBy(
          concat<SelectOption>(
            [{ label: 'Don’t Show This', value: '' }],
            map(range(HEIGHT_MIN, HEIGHT_MAX), (cm) => {
              if (isMetricSystem) {
                return {
                  label: `${cm}cm`,
                  value: String(cm)
                }
              }

              const totalInches = cm / 2.54

              return {
                label: `${Math.floor(totalInches / 12)}ft ${Math.floor(totalInches % 12)}in`,
                value: String(cm)
              }
            })
          ),
          'label'
        ),
      [isMetricSystem]
    )

    return (
      <SingleSelectInput
        displayValue={useMemo(
          () =>
            !value
              ? (placeholder ?? 'Don’t Show This')
              : isMetricSystem
                ? `${value}cm`
                : `${Math.floor(value / 2.54 / 12)}ft ${Math.floor(value / 2.54) % 12}in`,
          [placeholder, isMetricSystem, value]
        )}
        options={options}
        ref={ref}
        value={isNumber(value) ? String(value) : value}
        valueIndex={useMemo(() => {
          // since we store the value in centimeters, and those don't fully line up with feet/inches, we can't just
          // compare value with option.value. instead, loop over the options to find the last option with a value that is
          // not larger than the given value
          const selectedValue = value ?? HEIGHT_MID
          const valueIndex = reduce(
            options,
            (lastIndex, option, index) => {
              return Number(option.value) <= selectedValue ? index : lastIndex
            },
            -1
          )

          return valueIndex !== -1 ? valueIndex : 0
        }, [options, value])}
        {...props}
      />
    )
  }
)

HeightInput.displayName = 'HeightInput'
